import { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '../ui/button';

function isStandaloneDisplay() {
  return Boolean(
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone
  );
}

function isAppleTouchDevice() {
  const platform = window.navigator.platform || '';
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(platform) || (/Macintosh/.test(platform) && 'ontouchend' in document) || /iPad|iPhone|iPod/.test(ua);
}

export default function WorkerInstallPrompt({ appName = 'Workforce', compact = false }) {
  const [promptEvent, setPromptEvent] = useState(null);
  const [standalone, setStandalone] = useState(isStandaloneDisplay);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('workforceInstallDismissed') === 'true');
  const isApple = useMemo(() => isAppleTouchDevice(), []);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setPromptEvent(event);
      setDismissed(false);
    };
    const onInstalled = () => {
      setStandalone(true);
      setPromptEvent(null);
      localStorage.removeItem('workforceInstallDismissed');
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (standalone) return null;

  const handleInstall = async () => {
    if (!promptEvent) {
      setDismissed(true);
      localStorage.setItem('workforceInstallDismissed', 'true');
      return;
    }
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setPromptEvent(null);
    if (choice?.outcome !== 'accepted') {
      setDismissed(true);
      localStorage.setItem('workforceInstallDismissed', 'true');
    }
  };

  if (!promptEvent && dismissed) return null;

  return (
    <div className={`rounded-xl border border-teal-100 bg-teal-50/80 p-3 text-sm text-teal-950 ${compact ? 'mt-3' : ''}`} data-testid="worker-install-prompt">
      {promptEvent ? (
        <Button type="button" onClick={handleInstall} className="w-full gap-2 bg-teal-700 hover:bg-teal-800">
          <Download className="h-4 w-4" />
          Add {appName} to Home Screen
        </Button>
      ) : (
        <p>
          {isApple
            ? 'Open the Share menu, then choose Add to Home Screen.'
            : 'Open your browser menu and choose Install app or Add to Home Screen.'}
        </p>
      )}
    </div>
  );
}
